import { useCallback, useEffect, useRef, useState, type FocusEvent, type ReactNode } from 'react'
import { ChevronDown, User } from 'lucide-react'
import { tokens } from '@/theme'
import { TrelleborgMark } from './TrelleborgMark'
import { MOBILE_MAX } from './responsive'
import { Badge } from './SubNav'
import { useDismiss } from './useDismiss'
import type { NavTab } from '@/domain/types'
import { STAGE_GROUPS } from '@/domain/funnel'
import { CHAT_STARTERS } from '@/domain/chatStarters'

interface Props {
  active: NavTab
  /** Every link in the bar is a path; the shell routes it. */
  onNavigate: (path: string) => void
  /**
   * Chat → New thread: drops the conversation in memory and opens /chat. The
   * "Create ..." items pass a starter, sent as the new thread's first message.
   */
  onNewThread: (starter?: string) => void
  userName?: string
  onSignOut: () => void
  /** Time-off requests waiting for the signed-in manager. Shown on My work in the user menu. */
  pendingCount?: number
  /** The notification bell. A slot, so the header stays free of data hooks. */
  bell?: ReactNode
}

interface MenuItem {
  label: string
  path: string
  newThread?: boolean
  starter?: string
  /** A line under this item, separating it from the rest of the menu. */
  dividerAfter?: boolean
}

/**
 * Four tabs, each with a dropdown on hover or focus. `home` has no tab: the
 * mark is the route there. My work, Settings and the bug report sit in the
 * user menu on the right, which replaced the old My work tab, the
 * Organisation button and the Admin gear.
 *
 * Chat has no "My threads": a thread lives only in this tab's memory
 * (ChatProvider), so there is no list to show.
 */
const NAV: { id: NavTab; label: string; path: string; items: MenuItem[] }[] = [
  {
    id: 'chat',
    label: 'Chat',
    path: '/chat',
    items: [{ label: 'New thread', path: '/chat', newThread: true }],
  },
  {
    id: 'ideas',
    label: 'Ideas',
    path: '/ideas',
    items: [
      {
        label: 'Create an idea',
        path: '/chat',
        newThread: true,
        starter: CHAT_STARTERS.idea,
        dividerAfter: true,
      },
      { label: 'Board', path: '/ideas' },
    ],
  },
  {
    id: 'initiatives',
    label: 'Initiatives',
    path: '/initiatives',
    items: [
      {
        label: 'Create an initiative',
        path: '/chat',
        newThread: true,
        starter: CHAT_STARTERS.initiative,
        dividerAfter: true,
      },
      { label: 'All', path: '/initiatives' },
      ...STAGE_GROUPS.map(g => ({ label: g.label, path: `/initiatives?stage=${g.key}` })),
    ],
  },
  {
    id: 'people',
    label: 'People',
    path: '/people',
    items: [
      { label: 'People', path: '/people' },
      { label: 'My team', path: '/people?team=mine' },
    ],
  },
]

/**
 * Phone chrome is MobileChrome; these rules only cover a desktop window
 * dragged narrow before the shell swaps.
 */
const headerStyles = `
@media (max-width: ${MOBILE_MAX}px) {
  .keystone-header-inner { height: 56px !important; gap: 10px !important; }
  .keystone-header-tab { padding: 17px 8px !important; font-size: 10px !important; letter-spacing: 0.08em !important; }
  .keystone-header-tab > span { left: 8px !important; right: 8px !important; bottom: 12px !important; }
}
`

/**
 * Site chrome. The ink panel is the app's header on every route — each page
 * opens with a dark band that this sits flush against, so a paper header
 * would draw a line across the top of every hero.
 */
export function SiteHeader({
  active,
  onNavigate,
  onNewThread,
  userName,
  onSignOut,
  pendingCount = 0,
  bell,
}: Props) {
  // One dropdown at a time: hovering or focusing a tab replaces whichever was open.
  const [openId, setOpenId] = useState<NavTab | null>(null)
  // Leaving by mouse closes after a beat, so a pointer cutting a corner on its
  // way into the dropdown does not lose it.
  const closeTimer = useRef<number | null>(null)
  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current)
    closeTimer.current = null
  }, [])
  useEffect(() => cancelClose, [cancelClose])
  const openMenu = (id: NavTab) => {
    cancelClose()
    setOpenId(id)
  }
  const closeMenu = (id: NavTab) => {
    cancelClose()
    setOpenId(cur => (cur === id ? null : cur))
  }
  const leaveMenu = (id: NavTab) => {
    cancelClose()
    closeTimer.current = window.setTimeout(() => closeMenu(id), 150)
  }

  const pick = (item: MenuItem) => {
    cancelClose()
    setOpenId(null)
    if (item.newThread) onNewThread(item.starter)
    else onNavigate(item.path)
  }

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: tokens.inkPanel,
        borderBottom: `1px solid ${tokens.inkPanelLine}`,
      }}
    >
      <style>{headerStyles}</style>
      <div
        className="keystone-header-inner keystone-pad"
        style={{
          maxWidth: tokens.containerMax,
          margin: '0 auto',
          padding: `0 ${tokens.containerPadX}px`,
          height: tokens.headerH,
          display: 'grid',
          gridTemplateColumns: 'auto minmax(0, 1fr) auto',
          alignItems: 'center',
          gap: 32,
        }}
      >
        <TrelleborgMark size={40} onClick={() => onNavigate('/')} />

        <nav
          aria-label="Main"
          className="keystone-header-nav"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
        >
          {NAV.map(tab => (
            <NavTabWithMenu
              key={tab.id}
              label={tab.label}
              items={tab.items}
              active={active === tab.id}
              open={openId === tab.id}
              onOpen={() => openMenu(tab.id)}
              onLeave={() => leaveMenu(tab.id)}
              onClose={() => closeMenu(tab.id)}
              onClick={() => {
                cancelClose()
                setOpenId(null)
                onNavigate(tab.path)
              }}
              onPick={pick}
            />
          ))}
        </nav>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {bell}
          <UserMenu
            userName={userName}
            active={active === 'mywork' || active === 'settings' || active === 'bugs'}
            pendingCount={pendingCount}
            onNavigate={onNavigate}
            onSignOut={onSignOut}
          />
        </div>
      </div>
    </header>
  )
}

function NavTabWithMenu({
  label,
  items,
  active,
  open,
  onOpen,
  onLeave,
  onClose,
  onClick,
  onPick,
}: {
  label: string
  items: MenuItem[]
  active: boolean
  open: boolean
  onOpen: () => void
  onLeave: () => void
  onClose: () => void
  onClick: () => void
  onPick: (item: MenuItem) => void
}) {
  const lit = active || open
  // Focus leaving the tab and its menu together closes it; moving between them does not.
  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onClose()
  }
  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={onOpen}
      onMouseLeave={onLeave}
      onFocus={onOpen}
      onBlur={onBlur}
      onKeyDown={e => {
        if (e.key === 'Escape') onClose()
      }}
    >
      <button
        type="button"
        className="keystone-header-tab"
        aria-current={active ? 'page' : undefined}
        aria-expanded={open}
        onClick={onClick}
        style={{
          position: 'relative',
          flex: 'none',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '22px 14px',
          fontFamily: tokens.fontDisplay,
          fontSize: 11.5,
          fontWeight: active ? tokens.weightDemi : tokens.weightMedium,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          color: lit ? tokens.paper : tokens.inkPanelText,
          transition: `color ${tokens.durFast} ${tokens.easeStandard}`,
        }}
      >
        {label}
        <span
          style={{
            position: 'absolute',
            left: 14,
            right: 14,
            bottom: 18,
            height: 2,
            background: tokens.gold,
            transformOrigin: 'left center',
            transform: active ? 'scaleX(1)' : open ? 'scaleX(0.35)' : 'scaleX(0)',
            transition: `transform ${tokens.durStd} ${tokens.easeStandard}`,
          }}
        />
      </button>
      {open && (
        <ul aria-label={label} style={{ ...dropdown, left: 0, minWidth: 190 }}>
          {items.map(item => (
            <li key={item.label} style={item.dividerAfter ? dividerAfter : undefined}>
              <DropdownItem onClick={() => onPick(item)}>{item.label}</DropdownItem>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function UserMenu({
  userName,
  active,
  pendingCount,
  onNavigate,
  onSignOut,
}: {
  userName: string | undefined
  active: boolean
  pendingCount: number
  onNavigate: (path: string) => void
  onSignOut: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setOpen(false), [])
  useDismiss(ref, open, close)
  const go = (path: string) => {
    setOpen(false)
    onNavigate(path)
  }
  const lit = active || open
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label={[
          userName ? `Account: ${userName}` : 'Account',
          ...(pendingCount > 0 ? [`${pendingCount} pending`] : []),
        ].join(', ')}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          height: 34,
          padding: '0 10px',
          background: lit ? tokens.inkPanel2 : 'transparent',
          border: 'none',
          borderRadius: tokens.radiusMd,
          cursor: 'pointer',
          color: lit ? tokens.paper : tokens.inkPanelText,
          fontFamily: tokens.fontDisplay,
          fontSize: 12,
          fontWeight: tokens.weightMedium,
        }}
      >
        <User size={14} strokeWidth={1.6} />
        <span
          className="keystone-sm-hide"
          style={{
            maxWidth: 180,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {userName}
        </span>
        {/* The count moved off the old My work tab; it stays visible here so a
            manager does not have to open the menu to see it. */}
        {pendingCount > 0 && <Badge n={pendingCount} />}
        <ChevronDown size={13} strokeWidth={1.8} />
      </button>
      {open && (
        <ul aria-label="Account" style={{ ...dropdown, right: 0, minWidth: 200 }}>
          <li>
            <DropdownItem onClick={() => go('/my/time')}>
              My work
              {pendingCount > 0 && <Badge n={pendingCount} />}
            </DropdownItem>
          </li>
          <li>
            <DropdownItem onClick={() => go('/settings')}>Settings</DropdownItem>
          </li>
          <li>
            <DropdownItem onClick={() => go('/bugs')}>Report a bug</DropdownItem>
          </li>
          <li
            style={{ borderTop: `1px solid ${tokens.inkPanelLine}`, marginTop: 4, paddingTop: 4 }}
          >
            <DropdownItem
              onClick={() => {
                setOpen(false)
                onSignOut()
              }}
            >
              Sign out
            </DropdownItem>
          </li>
        </ul>
      )}
    </div>
  )
}

const dropdown = {
  position: 'absolute',
  top: '100%',
  zIndex: 110,
  listStyle: 'none',
  margin: 0,
  padding: '6px 0',
  background: tokens.inkPanel,
  border: `1px solid ${tokens.inkPanelLine}`,
  borderRadius: tokens.radiusMd,
  boxShadow: tokens.shadowDropdown,
} as const

const dividerAfter = {
  borderBottom: `1px solid ${tokens.inkPanelLine}`,
  marginBottom: 4,
  paddingBottom: 4,
} as const

function DropdownItem({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  const [hov, setHov] = useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onFocus={() => setHov(true)}
      onBlur={() => setHov(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        width: '100%',
        padding: '8px 16px',
        background: hov ? tokens.inkPanel2 : 'transparent',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: tokens.fontDisplay,
        fontSize: 13,
        fontWeight: tokens.weightMedium,
        color: hov ? tokens.paper : tokens.inkPanelText3,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  )
}
